import { useCallback, useEffect, useState } from "react";
import {
  fetchSalesOrgPricing,
  postSalesOrgPricingOp,
  type ManufacturerCatalogItemRow,
  type PricingLaneMode,
  type SalesCatalogItemRow,
  type SalesOrgPricingGetResponse,
  type SubscriberOverridePrefill,
} from "@/lib/salesOrgPricingClient";

export function useOrgPricingCatalogSection(organizationId: string) {
  const [data, setData] = useState<SalesOrgPricingGetResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const [csvText, setCsvText] = useState("");
  const [mfrCsvText, setMfrCsvText] = useState("");
  const [busy, setBusy] = useState(false);
  const [modeDraft, setModeDraft] = useState<PricingLaneMode>("auto");
  const [linkDraft, setLinkDraft] = useState("");
  const [salesCatalogItems, setSalesCatalogItems] = useState<SalesCatalogItemRow[]>([]);
  const [catalogTableSearch, setCatalogTableSearch] = useState("");
  const [manufacturerCatalogItems, setManufacturerCatalogItems] = useState<ManufacturerCatalogItemRow[]>([]);
  const [mfrCatalogSearch, setMfrCatalogSearch] = useState("");
  const [subscriberOverridePrefill, setSubscriberOverridePrefill] = useState<SubscriberOverridePrefill | null>(null);

  const refreshManufacturerCatalog = useCallback(async () => {
    if (!data) return;
    const inherit = modeDraft === "manufacturer_inherit";
    const linked = linkDraft.trim() || data.settings.linked_manufacturer_org_id || "";
    const mfrId = inherit ? linked : organizationId;
    if (inherit && !linked) {
      setManufacturerCatalogItems([]);
      return;
    }
    try {
      const j = await postSalesOrgPricingOp(organizationId, {
        op: "list_manufacturer_catalog_items",
        manufacturer_organization_id: mfrId,
        limit: 500,
      });
      setManufacturerCatalogItems((j.items as ManufacturerCatalogItemRow[]) ?? []);
    } catch {
      setManufacturerCatalogItems([]);
    }
  }, [organizationId, data, modeDraft, linkDraft]);

  useEffect(() => {
    void refreshManufacturerCatalog();
  }, [refreshManufacturerCatalog]);

  const pickManufacturerRowForOverride = useCallback((row: ManufacturerCatalogItemRow) => {
    const msrp = typeof row.msrp === "number" ? row.msrp : Number(row.msrp);
    setSubscriberOverridePrefill((prev) => ({
      revision: (prev?.revision ?? 0) + 1,
      product_line: row.product_line,
      width_ft: row.width_ft,
      length_ft: row.length_ft,
      package_key: row.package_key,
      options_key: row.options_key,
      base_msrp: Number.isFinite(msrp) ? msrp : 0,
      currency: row.currency || "USD",
    }));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchSalesOrgPricing(organizationId);
      setData(d);
      setModeDraft(d.settings.pricing_lane_mode);
      setLinkDraft(d.settings.linked_manufacturer_org_id ?? "");
      if (d.sales_snapshot?.has_active && d.sales_snapshot.tablesAvailable !== false) {
        try {
          const j = await postSalesOrgPricingOp(organizationId, { op: "list_sales_catalog_items", limit: 500 });
          setSalesCatalogItems((j.items as SalesCatalogItemRow[]) ?? []);
        } catch {
          setSalesCatalogItems([]);
        }
      } else {
        setSalesCatalogItems([]);
      }
    } catch (e: unknown) {
      setMsg({ text: e instanceof Error ? e.message : String(e), ok: false });
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyLaneSettings = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await postSalesOrgPricingOp(organizationId, {
        op: "patch_settings",
        pricing_lane_mode: modeDraft,
        linked_manufacturer_org_id: linkDraft.trim() || null,
      });
      setMsg({ text: "Pricing lane settings saved.", ok: true });
      await load();
    } catch (e: unknown) {
      setMsg({ text: e instanceof Error ? e.message : String(e), ok: false });
    } finally {
      setBusy(false);
    }
  };

  const validateCsv = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const j = await postSalesOrgPricingOp(organizationId, { op: "validate_sales_csv", csvText });
      const errs = (j.errors as { row: number; message: string }[] | undefined) ?? [];
      const warns = (j.warnings as { row: number; message: string }[] | undefined) ?? [];
      if (errs.length) {
        setMsg({ text: `Validation failed: ${errs.map((e) => `row ${e.row}: ${e.message}`).join("; ")}`, ok: false });
      } else {
        setMsg({
          text: `OK: ${(j.rows as unknown[] | undefined)?.length ?? 0} row(s). ${warns.map((w) => w.message).join(" ")}`,
          ok: true,
        });
      }
    } catch (e: unknown) {
      setMsg({ text: e instanceof Error ? e.message : String(e), ok: false });
    } finally {
      setBusy(false);
    }
  };

  const publishCsv = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const j = await postSalesOrgPricingOp(organizationId, { op: "publish_sales_csv", csvText });
      setMsg({
        text: `Published v${j.version} (${j.row_count} rows). ${((j.warnings as { message: string }[]) ?? []).map((w) => w.message).join(" ")}`,
        ok: true,
      });
      await load();
    } catch (e: unknown) {
      setMsg({ text: e instanceof Error ? e.message : String(e), ok: false });
    } finally {
      setBusy(false);
    }
  };

  const publishMfrCsv = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const j = await postSalesOrgPricingOp(organizationId, {
        op: "publish_manufacturer_csv",
        csvText: mfrCsvText,
        manufacturer_organization_id: organizationId,
      });
      setMsg({ text: `Manufacturer catalog updated (${j.row_count} rows).`, ok: true });
      await load();
    } catch (e: unknown) {
      setMsg({ text: e instanceof Error ? e.message : String(e), ok: false });
    } finally {
      setBusy(false);
    }
  };

  const upsertSubscriberOverride = async (payload: Record<string, unknown>) => {
    setBusy(true);
    setMsg(null);
    try {
      await postSalesOrgPricingOp(organizationId, { op: "upsert_subscriber_override", ...payload });
      setMsg({ text: "Subscriber override saved.", ok: true });
      await load();
    } catch (e: unknown) {
      setMsg({ text: e instanceof Error ? e.message : String(e), ok: false });
    } finally {
      setBusy(false);
    }
  };

  const deleteSubscriberOverride = async (id: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await postSalesOrgPricingOp(organizationId, { op: "delete_subscriber_override", id });
      setMsg({ text: "Override removed.", ok: true });
      await load();
    } catch (e: unknown) {
      setMsg({ text: e instanceof Error ? e.message : String(e), ok: false });
    } finally {
      setBusy(false);
    }
  };

  const updateSalesCatalogItem = async (id: string, patch: Record<string, unknown>) => {
    setBusy(true);
    setMsg(null);
    try {
      await postSalesOrgPricingOp(organizationId, { op: "update_sales_catalog_item", id, ...patch });
      setMsg({ text: "Catalog row updated.", ok: true });
      await load();
    } catch (e: unknown) {
      setMsg({ text: e instanceof Error ? e.message : String(e), ok: false });
    } finally {
      setBusy(false);
    }
  };

  const deleteSalesCatalogItem = async (id: string) => {
    if (!confirm("Delete this catalog line?")) return;
    setBusy(true);
    setMsg(null);
    try {
      await postSalesOrgPricingOp(organizationId, { op: "delete_sales_catalog_item", id });
      setMsg({ text: "Catalog row removed.", ok: true });
      await load();
    } catch (e: unknown) {
      setMsg({ text: e instanceof Error ? e.message : String(e), ok: false });
    } finally {
      setBusy(false);
    }
  };

  return {
    data,
    loading,
    msg,
    setMsg,
    csvText,
    setCsvText,
    mfrCsvText,
    setMfrCsvText,
    busy,
    load,
    modeDraft,
    setModeDraft,
    linkDraft,
    setLinkDraft,
    applyLaneSettings,
    validateCsv,
    publishCsv,
    publishMfrCsv,
    upsertSubscriberOverride,
    deleteSubscriberOverride,
    salesCatalogItems,
    catalogTableSearch,
    setCatalogTableSearch,
    updateSalesCatalogItem,
    deleteSalesCatalogItem,
    manufacturerCatalogItems,
    mfrCatalogSearch,
    setMfrCatalogSearch,
    subscriberOverridePrefill,
    pickManufacturerRowForOverride,
  };
}
